import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, InlineLoading, Tag } from "@carbon/react";
import { ArrowRight, Download } from "@carbon/icons-react";
import { ScreenAction } from "../../types";
import { downloadScreenAssistantFile } from "../../hooks/useScreenAssistant";

type Props = {
  action: ScreenAction;
  /** This card's answer is on its way. */
  answering: boolean;
  onConfirm: (actionId: string) => void;
  onCancel: (actionId: string) => void;
};

const STATUS_TAG: Record<
  ScreenAction["status"],
  "green" | "red" | "gray" | "blue"
> = {
  PROPOSED: "gray",
  RUNNING: "blue",
  DONE: "green",
  FAILED: "red",
  CANCELLED: "gray",
  EXPIRED: "gray",
};

/** A report the assistant prepared: ready from the start, nothing to confirm. */
const REPORT_KIND = "purchases_report";
/** A screen's own document (a bid report, a comparison, an OC's PDF), prepared the same way. */
const FILE_KIND = "screen_file";

/** Only the app's own paths are followed: a screen, or one of the assistant's files. */
const isDownload = (link: string) => link.startsWith("/api/screen-assistant/");
const isAppPath = (link: string) =>
  link.startsWith("/") && !link.startsWith("//") && !link.startsWith("/api/");

/**
 * A change the assistant proposed. The sentence is the server's, from the database, and nothing
 * happens until Confirm is clicked — the backend checks everything again at that moment. Once
 * done, it can open what it made, or download the file it prepared.
 */
const ScreenActionCard = ({
  action,
  answering,
  onConfirm,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const pending = action.status === "PROPOSED";
  const document = action.kind === FILE_KIND;
  const report = action.kind === REPORT_KIND || document;
  const link = action.status === "DONE" ? action.link : null;

  const download = async (path: string) => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadScreenAssistantFile(path);
    } catch {
      setDownloadFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  const linkButton = link ? (
    isDownload(link) ? (
      <Button
        size="sm"
        kind="tertiary"
        renderIcon={Download}
        disabled={downloading}
        onClick={() => download(link)}
      >
        {downloading
          ? t("screenAssistant.actions.downloading")
          : document
            ? t("screenAssistant.actions.downloadFile")
            : t("screenAssistant.actions.download")}
      </Button>
    ) : isAppPath(link) ? (
      <Button
        size="sm"
        kind="ghost"
        renderIcon={ArrowRight}
        onClick={() => navigate(link)}
      >
        {t("screenAssistant.actions.open")}
      </Button>
    ) : null
  ) : null;

  return (
    <div
      className="screen-assistant__action"
      role="group"
      aria-label={
        document
          ? t("screenAssistant.actions.fileLabel")
          : report
            ? t("screenAssistant.actions.reportLabel")
            : t("screenAssistant.actions.label")
      }
    >
      <p className="screen-assistant__action-summary">{action.summary}</p>
      {pending ? (
        answering ? (
          <InlineLoading description={t("screenAssistant.actions.running")} />
        ) : (
          <div className="screen-assistant__action-buttons">
            <Button
              size="sm"
              kind="primary"
              onClick={() => onConfirm(action.id)}
            >
              {t("screenAssistant.actions.confirm")}
            </Button>
            <Button size="sm" kind="ghost" onClick={() => onCancel(action.id)}>
              {t("screenAssistant.actions.cancel")}
            </Button>
          </div>
        )
      ) : (
        <div className="screen-assistant__action-result">
          {!report && (
            <Tag type={STATUS_TAG[action.status]} size="sm">
              {t(`screenAssistant.actions.status.${action.status}`)}
            </Tag>
          )}
          {action.outcome && <span>{action.outcome}</span>}
          {linkButton}
          {downloadFailed && (
            <span role="alert">
              {t("screenAssistant.actions.downloadError")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ScreenActionCard;
